import type { WorkerSchema } from '../types'
import { BILLIN_LINE_DEEP_LINK } from '../utils/billinLineDeepLink'

export const BillLinIncomeSchema: WorkerSchema = {
  workerId:     'bill-lin-income',
  workerLabel:  '💰 รายรับ',
  tabIcon:      'wallet',
  recordPrefix: 'BL',
  apiEndpoint:  '/api/v1/transactions?worker=bill-lin-income',
  // Queue/filter date = created_at (เวลาสร้าง), not last touch or transfer day.
  defaultSort:  { col: 'created_at', dir: 'desc' },
  searchFields: ['party_name','customer_name','sender_name','recipient_name','phone','items','tracking_no','remark'],

  columns: [
    { id:'created_at', header:'วันที่', type:'datetime', editable:false, sortable:true, visibility:'always', width:150 },
    { id:'record_date', header:'วันที่บันทึก', type:'datetime', editable:false, sortable:true, visibility:'hidden', width:150 },
    { id:'updated_at', header:'อัปเดตล่าสุด', type:'datetime', editable:false, sortable:true, visibility:'hidden', width:150 },
    { id:'party_name', header:'ผู้โอน/ลูกค้า', type:'text', editable:false, visibility:'always' },
    { id:'transfer_amount', header:'ยอดโอน', type:'amount', editable:false, sortable:true, visibility:'always', align:'right' },
    { id:'allocated_amount', header:'จับคู่แล้ว', type:'amount', editable:false, sortable:true, visibility:'always', align:'right' },
    { id:'unallocated_amount', header:'รอจัดสรร', type:'amount', editable:false, sortable:true, visibility:'always', align:'right' },
    { id:'money_type', header:'ประเภทเงิน', type:'text', editable:false, visibility:'always' },
    // Accounting Handoff status (plan §2.1) — read-only
    { id:'accounting_export', header:'ส่งบัญชี', type:'text', editable:false, visibility:'always' },
    { id:'status', header:'สถานะ', type:'status', editable:false, visibility:'always' },
    // Live mirror status only (plan §20 G0) — not the accounting export column
    { id:'google_mirror', header:'สำเนา Google', type:'text', editable:false, visibility:'always' },
    { id:'image_url', header:'สลิป', type:'image', editable:false, visibility:'hidden' },
    { id:'sender_bank', header:'ธนาคาร', type:'bank', editable:false, visibility:'hidden' },
    { id:'record_id', header:'รหัส', type:'id', editable:false, copyable:true, visibility:'hidden' },
    { id:'reference_no', header:'อ้างอิง', type:'text', editable:false, copyable:true, visibility:'hidden' },
    { id:'recipient_account', header:'บัญชีรับเงิน', type:'text', editable:false, copyable:true, visibility:'hidden' },
    { id:'recipient_name', header:'ชื่อผู้รับของ', type:'text', editable:true, visibility:'hidden', configGate:['A'] },
    { id:'province', header:'จังหวัด', type:'text', editable:true, copyable:true, visibility:'hidden', configGate:['A'] },
    { id:'address', header:'ที่อยู่', type:'text', editable:true, copyable:true, visibility:'hidden', configGate:['A'] },
    { id:'tracking_no', header:'เลขพัสดุ', type:'text', editable:true, visibility:'hidden', configGate:['A'] },
    { id:'items', header:'รายการ', type:'text', editable:true, visibility:'hidden' },
    { id:'phone', header:'เบอร์โทร', type:'tel', editable:true, visibility:'hidden' },
    { id:'remark', header:'หมายเหตุ', type:'text', editable:true, visibility:'hidden' },
    { id:'member_ref', header:'รหัสสมาชิก/นัด', type:'text', editable:false, copyable:true, visibility:'hidden', configGate:['C','D'] },
    { id:'extra_info', header:'ข้อมูลเพิ่มเติม', type:'text', editable:true, visibility:'hidden', configGate:['C','D'] },
    { id:'branch', header:'สาขา', type:'text', editable:true, visibility:'hidden', scaleGate:'Multi-Branch' },
    { id:'sender_name', header:'ผู้โอน', type:'text', editable:false, visibility:'hidden' },
    { id:'customer_name', header:'ชื่อลูกค้า', type:'text', editable:true, visibility:'hidden' },
    { id:'slip_matched_amount', header:'จับคู่แล้ว (legacy)', type:'amount', editable:false, visibility:'hidden', align:'right' },
    { id:'job_type', header:'ประเภทงาน', type:'text', editable:false, visibility:'hidden' },
    { id:'total_amount', header:'ยอดรวม (฿)', type:'amount', editable:false, visibility:'hidden', align:'right' },
    { id:'transfer_datetime', header:'วันเวลาโอน', type:'datetime', editable:false, visibility:'hidden', width:150 },
    { id:'postcode', header:'รหัสไปรษณีย์', type:'text', editable:true, copyable:true, visibility:'hidden', configGate:['A'] },
  ],

  drawerSections: [
    { label: 'PAYMENT', fields: ['transfer_amount','allocated_amount','unallocated_amount','money_type','accounting_export','google_mirror','total_amount','transfer_datetime','reference_no','sender_bank','recipient_account','image_url'] },
    { label: 'CUSTOMER', fields: ['party_name','sender_name','customer_name','phone'] },
    { label: 'SHIPPING', fields: ['recipient_name','province','postcode','address','tracking_no'], configGate: ['A'] },
    { label: 'DETAILS', fields: ['items','member_ref','extra_info','branch','remark','record_id'] },
  ],

  filterOptions: {
    status: ['✅ จับคู่สำเร็จ','✅ ตรวจสอบแล้ว','✅ ต่ออายุแล้ว','✅ ปิดงานแล้ว',
             '⚠️ ไม่มีเบอร์','⚠️ ยอดไม่ตรง','🔴 บันทึกล้มเหลว','❌ ยกเลิก'],
    datePresets: ['วันนี้','7 วันที่ผ่านมา','เดือนนี้','เดือนที่แล้ว','กำหนดเอง'],
  },

  emptyState: {
    icon: '📨',
    title: 'ยังไม่มีรายการ',
    body: 'forward สลิปไปที่ @HireAera เพื่อเริ่มบันทึก',
    cta: { label: 'เปิด LINE แชท @HireAera', href: BILLIN_LINE_DEEP_LINK },
  },

  drawerActions: [
    { id: 'download-summary', label: 'ดาวน์โหลดสรุป', variant: 'primary' },
    { id: 'cancel-record',    label: 'ยกเลิกรายการ',  variant: 'secondary' },
  ],
  historyEnabled: true,
}
